import React, { FunctionComponent, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { LockIcon, SearchIcon } from "../common/sidebar/icons";
import { useAuthentication } from "../../hooks/useAuthentication";
import { ContentLayout } from "../common/Layout";
import { useUserContext } from "../../contexts/UserContext";
import { useUsers } from "../../hooks/useUsers";
import FirebaseActions from "../../actions/firebase/FirebaseActions";
import { AccessControlModal } from "./AccessControlModal";
import { PlusToggle } from "./PlusToggle";

interface Props {
  isAuthenticated: boolean;
}

const Admin: FunctionComponent<Props> = ({ isAuthenticated }) => {
  const navigate = useNavigate();
  const { setActiveUser } = useUserContext();
  const [users, usersLoading, usersError] = useUsers();
  const [modalUserId, setModalUserId] = useState<string | null>(null);
  const [modalAccessControl, setModalAccessControl] = useState<Record<string, boolean>>({});
  const [isModalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState("");

  useAuthentication(isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  if (usersError) {
    console.error(usersError);
  }

  const handleUserSelect = (userId: string): void => {
    const user = users.find((u) => u.id === userId);
    if (user) {
      setActiveUser(user);
      navigate(`/user/${userId}/dashboard`);
    }
  };

  const openModal = async (userId: string): Promise<void> => {
    try {
      const accessControlData = await FirebaseActions.getAccessControlData(userId);
      setModalUserId(userId);
      setModalAccessControl(accessControlData.allowedUsers);
      setModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch access control data:", error);
    }
  };

  const closeModal = (): void => {
    setModalUserId(null);
    setModalOpen(false);
  };

  const handleSaveAccessControl = async (
    userId: string,
    updatedAccessControl: Record<string, boolean>
  ): Promise<void> => {
    try {
      await FirebaseActions.updateAccessControl(userId, updatedAccessControl);
      console.log("Access control updated successfully");
      closeModal();
    } catch (error) {
      console.error("Failed to update access control:", error);
    }
  };

  const term = search.trim().toLowerCase();
  const visibleUsers = term
    ? users.filter((user) =>
        [user.userProfile?.name, user.userProfile?.email, user.email, user.id].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(term)
        )
      )
    : users;

  return (
    <ContentLayout
      title="Admin"
      siteTitle="Admin Control Panel"
      subtitle={`${users.length} Konten`}
      isLoading={usersLoading}
      actions={
        <label className="header-search">
          <SearchIcon style={{ color: "var(--text-faint)" }} />
          <input
            value={search}
            placeholder="Konto suchen …"
            aria-label="Konto suchen"
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
      }>
      <section className="panel">
        <div className="data-row data-row--head data-row--account">
          <span>Konto</span>
          <span>E-Mail</span>
          <span>Plus</span>
          <span className="text-end">Aktionen</span>
        </div>

        <div className="data-rows">
          {visibleUsers.map((user) => (
            <div className="data-row data-row--account" key={user.id}>
              <div className="data-row__primary">{user.userProfile?.name || "Ohne Namen"}</div>
              <span className="data-row__muted">{user.userProfile?.email || user.email}</span>
              <PlusToggle userId={user.id} />
              <div className="data-row__actions">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleUserSelect(user.id)}>
                  Dashboard
                </button>
                <button
                  type="button"
                  title="Zugriff verwalten"
                  className="btn btn-secondary btn-sm btn-icon-square"
                  onClick={() => void openModal(user.id)}>
                  <LockIcon />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {modalUserId && (
        <AccessControlModal
          show={isModalOpen}
          onClose={closeModal}
          onSave={handleSaveAccessControl}
          userId={modalUserId}
          allowedUsers={modalAccessControl}
          allUsers={users}
        />
      )}
    </ContentLayout>
  );
};

export default Admin;
